import { Controller } from "react-hook-form";
import { SIMPLInput } from "../../components/commons/input/SIMPLInput";
import { SIMPLSelectDropdown } from "../../components/commons/input/SIMPLSelectDropdown";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { UseFormCloudEnvironment } from "./UseFormCloudEnvironment";
import { useEffect, useState } from "react";
import { getProviders } from "~/Utils/Utils";
import { REGEX } from "~/resources/Regex";
import { SIMPLBreadcrumb } from "../../components/commons/breadcrumb/SimplBreadCrumb";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";
import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { useNavigate } from "react-router-dom";
import { useGetCloudEnvironmentsQuery } from "~/services/cloudenvironment";
import { useTranslation } from "react-i18next";

export const AddCloudEnvironment = () => {
  const { refetch } = useGetCloudEnvironmentsQuery({ page: 0, size: 999, active: true });
  const {
    handleSubmit,
    control,
    errors,
    isValid,
    isSubmitting,
    submitForm,
    provider,
    isLoading,
    isError
  } = UseFormCloudEnvironment(refetch);

  const navigate = useNavigate();
  const [cloudProviderList, setCloudProviderList] = useState<any[]>([]);
  const { t } = useTranslation();

  // Load provider list when API returns
  useEffect(() => {
    if (provider) {
      setCloudProviderList(getProviders(provider));
    }
  }, [provider]);

  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSubmit(submitForm)(event);
  };

  const handleCancel = () => {
    navigate("/cloud-environment");
    console.log("Form cancelled");
  };

  return (
    <SIMPLMenuHeader>
      <LoadingWrapper
        loadingTitle={t("LoadingCloudEnvironments")}
        isLoading={isLoading}
        isError={isError}
        testId="cloud-env-loading"
      >
        <div className="flex justify-center bg-white">
          <div className="w-full max-w-[1104px] rounded-xl border border-[#D6D6D6] bg-white">
            <div className="pt-4 px-4">
              <SIMPLBreadcrumb />
              <h1 className="mt-2 font-['Arial'] text-[28px] font-bold leading-[44px] text-left text-Foundation/Heading normal-case">
                {t("CloudEnvironment")}
              </h1>
            </div>

            <form onSubmit={handleFormSubmit}>
              <div className="pt-4 space-y-2 px-4 pb-4">
                <SIMPLInput
                  name="environmentName"
                  control={control}
                  rules={{
                    required: { value: true, message: t('NameRequired') },
                    pattern: { value: REGEX.EnvironmentNameRegex, message: t('MessageRegexEnvironmentName') },
                    maxLength: { value: 63, message: t('NameTooLong') },
                  }}
                  messageRequired={t('NameRequired')}
                  label={t("Name")}
                  placeholder={t("Name")}
                  required
                  id="environmentName"
                  fontWeight="bold"
                  errorLabel={errors.environmentName?.message}
                  testId="cloud-env-name"
                />

                <SIMPLInput
                  name="environmentDescription"
                  control={control}
                  label={t("Description")}
                  rules={{
                    pattern: { value: REGEX.CloudEnvDescriptions, message: t('MessageRegexEnvironmentDescriptions') },
                  }}
                  id="environmentDescription"
                  fontWeight="bold"
                  errorLabel={errors.environmentDescription?.message}
                  placeholder={t("Description")}
                  testId="cloud-env-descriptions"
                />

                <SIMPLInput
                  name="datacenterName"
                  control={control}
                  rules={{ required: { value: true, message: t('MessageDataCenterNameRequired') } }}
                  label={t("DataCenterName")}
                  messageRequired={t('MessageDataCenterNameRequired')}
                  placeholder={t("DataCenterName")}
                  required
                  id="datacenterName"
                  fontWeight="bold"
                  errorLabel={errors.datacenterName?.message}
                  testId="cloud-env-datacenter-name"
                />

                <SIMPLInput
                  name="datacenterDescription"
                  control={control}
                  label={t("DataCenterDescription")}
                  rules={{
                    pattern: { value: REGEX.CloudEnvDescriptions, message: t('MessageRegexEnvironmentDescriptions') },
                  }}
                  placeholder={t("DataCenterDescription")}
                  id="datacenterDescription"
                  fontWeight="bold"
                  errorLabel={errors.datacenterDescription?.message}
                  testId="cloud-env-datacenter-descriptions"
                />

                <div className="grid gap-6 md:grid-cols-2">
                  <SIMPLInput
                    name="location"
                    control={control}
                    rules={{ required: { value: true, message: t('MessageLocationRequired') } }}
                    label={t("Location")}
                    messageRequired={t('MessageLocationRequired')}
                    required
                    id="location"
                    fontWeight="bold"
                    errorLabel={errors.location?.message}
                    placeholder={t("Location")}
                    testId="cloud-env-location"
                  />

                  <SIMPLInput
                    name="iac"
                    control={control}
                    rules={{ required: { value: true, message: t('MessageIacRequired') } }}
                    label={t("Iac")}
                    messageRequired={t('MessageIacRequired')}
                    required
                    id="iac"
                    fontWeight="bold"
                    errorLabel={errors.iac?.message}
                    placeholder={t("Iac")}
                    testId="cloud-env-iac"
                  />

                  {/* Cloud Provider dropdown */}
                  <Controller
                    name="cloudProviderId"
                    control={control}
                    rules={{
                      validate: (value) => (value ? true : t('MessageCloudProviderRequired')),
                      required:t('MessageCloudProviderRequired')
                    }}
                   
                    render={({ field }) => (
                      <SIMPLSelectDropdown
                        id="cloudProviderId"
                        
                        label={t("CloudProvider")}
                        options={cloudProviderList.map((cp) => ({
                          label: cp.cloudProviderName,
                          value: cp.id,
                        }))}
                        value={field.value || ""}
                        onChange={field.onChange}
                        errorLabel={errors.cloudProviderId?.message as string}
                        placeholder={t("CloudProvider")}
                        testId="cloud-env-cloud-provider"
                        background="white"
                        hideErrorLabel={false}
                        isRequired
                      />
                    )}
                  />
                </div>

                <SIMPLInput
                  name="authenticationConfig"
                  type="textarea"
                  rows={4}
                  control={control}
                  label={t("AuthenConfig")}
                  required
                  id="authenticationConfig"
                  fontWeight="bold"
                  rules={{
                    validate: (value: string) => {
                      if (!value?.trim()) return true;
                      try {
                        JSON.parse(value);
                        return true;
                      } catch {
                        return t("InvalidJsonFormat");
                      }
                    },
                  }}
                  messageRequired={t('MessageAuthConfigRequired')}
                  errorLabel={errors.authenticationConfig?.message}
                  placeholder={t("EnterValidJson")}
                  testId="cloud-env-auth-config"
                />
              </div>

              {/* ACTION BUTTONS – FULL WIDTH */}
              <div className="w-full flex justify-end gap-3 bg-[#F5F5F5] border-t border-[#D6D6D6] rounded-b-xl px-4 py-3">
                <SIMPLButtonCustomized
                  label={t("Cancel")} 
                  secondary
                  buttonHandlerAction={handleCancel}
                  testId="cloud-env-button-cancel"
                />
                <SIMPLButtonCustomized
                  label={t("Submit")}
                  primary
                  disabled={!isValid || isSubmitting}
                  testId="cloud-env-button-submit"
                />
              </div>
            </form>
          </div>
        </div>
      </LoadingWrapper>
    </SIMPLMenuHeader>
  );
};